import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactTusOlayi,
  type ReactNode,
} from "react";
import { cn } from "./cn";

/**
 * SEKME PANELİ — panel DEĞİŞTİREN sekmeler
 *
 * ================================================================
 * `Sekmeler` VARKEN NEDEN İKİNCİ BİR BİLEŞEN
 * ================================================================
 * `Sekmeler.tsx` bilinçli olarak ARIA sekme deseni DEĞİL: çipleri
 * `aria-pressed` ile çiziyor ve gerekçesini kendi başlığında yazıyor —
 * o çipler aynı listeyi SÜZÜYOR, panel değiştirmiyor. Basılı kalan
 * düğme tam olarak bunu anlatıyor.
 *
 * 360° öğrenci ekranı ise gerçekten panel değiştiriyor: her sekmenin
 * arkasında ayrı bir ekran var. Orada `aria-pressed` yanlış olurdu —
 * ekran okuyucu "basılı düğme" der, oysa kullanıcı bir bölümden
 * ötekine geçti.
 *
 * `Sekmeler`in notu doğru olanı da söylüyor: desenin YARISINI yazmak
 * hiç yazmamaktan kötü. Bu dosya bu yüzden desenin TAMAMINI kuruyor —
 * gezinen odak (roving tabindex), ok tuşları, Home/End ve her sekmenin
 * kendi `tabpanel`ine bağlanması. Eksik bırakılan hiçbir parça yok;
 * `SekmePaneli.test.tsx` dördünü de ölçüyor.
 *
 * ================================================================
 * SEÇİM DIŞARIDA TUTULUYOR
 * ================================================================
 * Hangi sekmenin açık olduğunu bu bileşen SAKLAMIYOR. Saklasaydı
 * adres çubuğuna sekme yazmak (`?sekme=denemeler`) ya da bir yerden
 * doğrudan "Denemeler"e atlamak imkânsız olurdu — oysa 360° ekranında
 * en çok istenecek şey tam olarak bu.
 */

export interface SekmePaneliSekmesi<T extends string> {
  deger: T;
  etiket: string;
}

export interface SekmePaneliProps<T extends string> {
  sekmeler: readonly SekmePaneliSekmesi<T>[];
  secili: T;
  onSec: (deger: T) => void;
  /**
   * Sekme şeridinin ekran okuyucuya verilen adı.
   *
   * Zorunlu: adsız bir `tablist`, sayfada başka sekme grupları varken
   * hangisinde olduğunu söylemiyor.
   */
  etiket: string;
  /**
   * `id` öneki — aynı sayfada iki sekme paneli olursa kimlikler
   * çakışmasın diye. Çakışan `id`, `aria-controls` bağını sessizce
   * yanlış panele bağlardı.
   */
  kimlikOneki: string;
  /**
   * Şeritte DOĞRUDAN görünen sekme sayısı; fazlası "Diğer" menüsüne girer
   * (UI/UX Dalga 2). Verilmezse hepsi şeritte (geriye uyumlu).
   *
   * SEÇİLİ SEKME MENÜDE KALAMAZ: `tabpanel`in `aria-labelledby`si var olan bir
   * sekmeyi göstermeli ve seçili bölümün adı ekranda görünmeli. Menüden seçilen
   * sekme şeritte son sırada belirir ve menüden çıkar; yani şerit en fazla
   * `gorunenAdet + 1` sekme taşıyor.
   */
  gorunenAdet?: number;
  /** Seçili sekmenin içeriği */
  children: ReactNode;
  className?: string;
}

export function SekmePaneli<T extends string>({
  sekmeler,
  secili,
  onSec,
  etiket,
  kimlikOneki,
  gorunenAdet,
  children,
  className,
}: SekmePaneliProps<T>) {
  const seritRef = useRef<HTMLDivElement>(null);
  const digerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuKimligi = useId();
  const [menuAcik, setMenuAcik] = useState(false);

  const tasma = gorunenAdet !== undefined && gorunenAdet < sekmeler.length;
  const ilkler = tasma ? sekmeler.slice(0, gorunenAdet) : sekmeler;
  const seciliSekme = sekmeler.find((s) => s.deger === secili);
  const seritSekmeleri =
    tasma && seciliSekme && !ilkler.includes(seciliSekme) ? [...ilkler, seciliSekme] : ilkler;
  const menuSekmeleri = tasma ? sekmeler.filter((s) => !seritSekmeleri.includes(s)) : [];

  const sekmeKimligi = (d: T) => `${kimlikOneki}-sekme-${d}`;
  const panelKimligi = (d: T) => `${kimlikOneki}-panel-${d}`;

  /*
    ================================================================
    SEÇİLİ SEKME GÖRÜNÜR ALANA KAYDIRILIYOR
    ================================================================
    On dört sekme telefonda ekrana sığmıyor; şerit yatay kayıyor.
    Kaydırma olmasaydı klavyeyle ilerleyen kullanıcı odağı ekran
    DIŞINDA bir düğmeye taşır ve "hiçbir şey olmadı" sanırdı.

    `block: "nearest"` şart: varsayılan `"start"` bütün sayfayı dikey
    olarak da zıplatıyor.
  */
  useEffect(() => {
    const dugme = seritRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    /*
      `typeof` kontrolu SUS PAYI DEGIL: jsdom `scrollIntoView`u hic
      tanimlamiyor ve cagri butun bileseni cokertiyor. Yani bu satir
      olmadan sekme panelinin TESTLERI kosamiyor — kaydirma davranisi
      degil, olculebilirligin kendisi buna bagli.
    */
    if (typeof dugme?.scrollIntoView === "function") {
      dugme.scrollIntoView({ inline: "nearest", block: "nearest" });
    }
  }, [secili]);

  /*
    ================================================================
    GEZİNEN ODAK: SEÇİM DEĞİŞİNCE ODAK DA TAŞINIYOR
    ================================================================
    APG'nin otomatik etkinleşen sekme deseni: ok tuşu hem seçimi hem
    ODAĞI taşır. Yalnızca seçimi taşısaydık odak eski düğmede kalır ve
    ikinci ok tuşu yine oradan hesaplanırdı — kullanıcı ikinci sekmeye
    gidip saplanırdı.
  */
  const tasi = (yon: 1 | -1 | "bas" | "son") => {
    const i = seritSekmeleri.findIndex((s) => s.deger === secili);
    if (i === -1) return;
    const hedef =
      yon === "bas"
        ? 0
        : yon === "son"
          ? seritSekmeleri.length - 1
          : // Uclarda BASA/SONA saran gezinme — APG'nin onerdigi.
            (i + yon + seritSekmeleri.length) % seritSekmeleri.length;
    const s = seritSekmeleri[hedef];
    if (!s) return;
    onSec(s.deger);
    // Odak yeni dugmeye: `onSec` state degistirdigi icin DOM'un
    // guncellenmesini beklemek gerekmiyor — dugme zaten cizili, yalnizca
    // `tabIndex`i degisecek ve `focus()` ona ragmen calisiyor.
    seritRef.current?.querySelector<HTMLElement>(`#${CSS.escape(sekmeKimligi(s.deger))}`)?.focus();
  };

  /*
    ================================================================
    "DİĞER" MENÜSÜ — UI/UX Dalga 2 (M2)
    ================================================================
    Menü ARIA `menu` deseni: açılınca odak ilk öğede, ok tuşları/Home/End
    öğeler arasında, Escape kapatıp odağı "Diğer" düğmesine geri veriyor,
    dışarı tıklamak kapatıyor. Menü, `tablist`in İÇİNDE değil kardeşi:
    `tablist`in çocukları yalnızca `tab` olabilir.
  */
  useEffect(() => {
    if (!menuAcik) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const disariTikla = (e: MouseEvent): void => {
      const hedef = e.target as Node | null;
      if (hedef && (menuRef.current?.contains(hedef) || digerRef.current?.contains(hedef))) return;
      setMenuAcik(false);
    };
    document.addEventListener("mousedown", disariTikla);
    return () => document.removeEventListener("mousedown", disariTikla);
  }, [menuAcik]);

  const menuyuKapat = (odagiVer: boolean): void => {
    setMenuAcik(false);
    if (odagiVer) digerRef.current?.focus();
  };

  const menuTusu = (e: ReactTusOlayi<HTMLDivElement>): void => {
    const ogeler = [...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    if (ogeler.length === 0) return;
    const i = ogeler.indexOf(document.activeElement as HTMLElement);
    const odakla = (n: number): void => {
      e.preventDefault();
      ogeler[(n + ogeler.length) % ogeler.length]?.focus();
    };
    if (e.key === "ArrowDown") odakla(i + 1);
    else if (e.key === "ArrowUp") odakla(i - 1);
    else if (e.key === "Home") odakla(0);
    else if (e.key === "End") odakla(ogeler.length - 1);
    else if (e.key === "Escape") {
      e.preventDefault();
      menuyuKapat(true);
    } else if (e.key === "Tab") menuyuKapat(false);
  };

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="relative flex items-end">
        <div
          ref={seritRef}
          role="tablist"
          aria-label={etiket}
          aria-orientation="horizontal"
          // `overflow-x-auto`: on dort sekme telefona sigmiyor. Sayfa
          // GOVDESI yatay kaymamali (mobil oncelik) — kayma serit icinde.
          className="border-notr-200 -mx-1 flex min-w-0 flex-1 gap-1 overflow-x-auto border-b px-1"
          onKeyDown={(e) => {
            const yon =
              e.key === "ArrowRight"
                ? 1
                : e.key === "ArrowLeft"
                  ? -1
                  : e.key === "Home"
                    ? "bas"
                    : e.key === "End"
                      ? "son"
                      : null;
            if (yon === null) return;
            e.preventDefault();
            tasi(yon);
          }}
        >
          {seritSekmeleri.map((s) => {
            const acik = s.deger === secili;
            return (
              <button
                key={s.deger}
                id={sekmeKimligi(s.deger)}
                type="button"
                role="tab"
                aria-selected={acik}
                aria-controls={panelKimligi(s.deger)}
                /*
                GEZİNEN ODAK: şeritte SEKME TUŞUNA yalnızca bir düğme
                cevap veriyor. Hepsi `0` olsaydı klavye kullanıcısı
                içeriğe ulaşmak için on dört kez Sekme'ye basardı.
              */
                tabIndex={acik ? 0 : -1}
                onClick={() => onSec(s.deger)}
                className={cn(
                  // Olculer `Sekmeler.tsx` ile ayni: 44px dokunma
                  // yuksekligi, 13px yan dolgu, 13px/500 yazi. Iki bilesen
                  // ayni panelde yan yana gorunuyor; farkli olculer
                  // "baska bir sey" izlenimi verirdi.
                  "min-h-dokunma rounded-t-alan inline-flex shrink-0 items-center px-[13px] text-[13px] font-medium",
                  "ease-cikis transition-colors duration-(--sure-anlik)",
                  // Alt cizgi seridin `border-b`si uzerine biniyor: secili
                  // sekme ile paneli arasindaki bag GORSEL olarak da kurulu.
                  acik
                    ? "text-marka-700 border-marka-500 -mb-px border-b-2 bg-transparent"
                    : "text-notr-700 hover:bg-notr-100 border-b-2 border-transparent",
                )}
              >
                {s.etiket}
              </button>
            );
          })}
        </div>

        {menuSekmeleri.length > 0 && (
          <div className="border-notr-200 flex shrink-0 items-end border-b">
            <button
              ref={digerRef}
              type="button"
              aria-haspopup="menu"
              aria-expanded={menuAcik}
              aria-controls={menuAcik ? menuKimligi : undefined}
              onClick={() => setMenuAcik((a) => !a)}
              className={cn(
                "min-h-dokunma rounded-t-alan inline-flex shrink-0 items-center gap-1 px-[13px] text-[13px] font-medium",
                "ease-cikis transition-colors duration-(--sure-anlik)",
                "text-notr-700 hover:bg-notr-100 border-b-2 border-transparent",
              )}
            >
              Diğer
              <span aria-hidden="true" className="text-[10px]">
                ▾
              </span>
            </button>
          </div>
        )}

        {menuAcik && menuSekmeleri.length > 0 && (
          <div
            ref={menuRef}
            id={menuKimligi}
            role="menu"
            aria-label={`${etiket} — diğer bölümler`}
            onKeyDown={menuTusu}
            className="border-notr-200 bg-notr-0 shadow-kart rounded-alan absolute top-full right-0 z-30 mt-1 flex min-w-56 flex-col border p-1"
          >
            {menuSekmeleri.map((s) => (
              <button
                key={s.deger}
                type="button"
                role="menuitem"
                onClick={() => {
                  onSec(s.deger);
                  menuyuKapat(false);
                }}
                className="min-h-dokunma rounded-alan text-notr-800 hover:bg-notr-100 focus-visible:bg-notr-100 px-3 text-left text-sm"
              >
                {s.etiket}
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        id={panelKimligi(secili)}
        role="tabpanel"
        aria-labelledby={sekmeKimligi(secili)}
        /*
          `tabIndex={0}`: panelin ICINDE odaklanabilir bir sey olmayabilir
          (yalnizca metin). O durumda klavye kullanicisi panele hic
          ulasamaz ve icerigi ekran okuyucuyla okuyamazdi.
        */
        tabIndex={0}
        className="rounded-alan focus-visible:outline-marka-500 focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        {children}
      </div>
    </div>
  );
}
